import { computed, effect, inject, untracked } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';

import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { AppError, FileSaveService, SpinnerService } from '@hglobal/fin-ui';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withComputed, withHooks, withMethods, withProps, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import dayjs from 'dayjs';
import { from, mergeMap, switchMap, tap, toArray } from 'rxjs';
import { catchError } from 'rxjs/operators';

import {
  setErrorStatusFor,
  setPendingStatusFor,
  setSuccessStatusFor,
  StoreHelperService,
  withRequestHandler,
  withRequestStatus,
} from '@kr/core';
import { WalletCard } from '@kr/models';

import { MASS_PAYOUT_API } from './api';
import { Chunk, ChunkEstimatesAndRiskScoreReq, DownloadCsvReq, MassPayOut, MassPayoutState, PayOutsFilters } from './models';

const initialState: MassPayoutState = {
  massPayOutExecuted: null,
  progressBar: 0,
  massPayOutEstimates: null,
};

export const MassPayoutStore = signalStore(
  withDevtools('massPayout'),
  withState(initialState),
  withRequestStatus(['getEstimatesAndRiskScores', 'executeMassPayout', 'downloadCsv']),
  withRequestHandler(),
  withProps(store => ({
    store,
    massPayOutApi: inject(MASS_PAYOUT_API),
    storeHelperService: inject(StoreHelperService),
    spinnerService: inject(SpinnerService),
    fileSave: inject(FileSaveService),
    downloadConfig: {
      api: 'wallets/screen/download/',
      translationKey: 'massWalletScreenResult.btn.saveAsCsv',
    },
  })),
  withComputed(({ massPayOutExecuted }) => ({
    totalSuccess: computed(() => massPayOutExecuted().reduce((acc, item) => (item.statusOriginal === 'completed' ? acc + 1 : acc), 0)),
    totalFailed: computed(() => massPayOutExecuted().reduce((acc, item) => (item.statusOriginal === 'rejected' ? acc + 1 : acc), 0)),
    totalExecutedCount: computed(() => massPayOutExecuted().length || 0),
  })),
  withMethods(({ store, massPayOutApi, fileSave }) => ({
    getEstimatesAndRiskScores: rxMethod<{
      chunks: ChunkEstimatesAndRiskScoreReq;
      limitPerChunk: number;
      chunksCount: number;
      providerId: number;
      wallet: WalletCard;
    }>(
      switchMap(({ chunks, limitPerChunk, chunksCount, wallet, providerId }) => {
        patchState(store, setPendingStatusFor('getEstimatesAndRiskScores'));

        const requests$ = Object.values(chunks).map((chunk: unknown) => {
          return massPayOutApi.getEstimatesAndRiskScores({ chunk: chunk as Chunk[] }, wallet.id.toString(), providerId).pipe(
            tap(() => patchState(store, { progressBar: store.progressBar() + Math.floor(100 / chunksCount) })),
            catchError((error: AppError) => {
              patchState(store, setErrorStatusFor('getEstimatesAndRiskScores', error));
              throw error;
            }),
          );
        });

        return from(requests$).pipe(
          mergeMap(chunkObservable => chunkObservable, limitPerChunk),
          toArray(),
          tapResponse({
            next: (res: MassPayOut[]) => {
              const massPayOut = combineMassPayOutData(res);
              patchState(store, { massPayOutEstimates: massPayOut }, setSuccessStatusFor('getEstimatesAndRiskScores'));
            },
            error: (error: AppError) => {
              patchState(store, setErrorStatusFor('getEstimatesAndRiskScores', error));
            },
          }),
        );
      }),
    ),
    executeMassPayout: rxMethod<{ body: MassPayOut; walletId: string; orgId: number }>(
      switchMap(({ body, walletId, orgId }) => {
        patchState(store, setPendingStatusFor('executeMassPayout'));
        return massPayOutApi.executeMassPayout(body, walletId, orgId).pipe(
          tapResponse({
            next: massPayOutExecuted => {
              patchState(store, { massPayOutExecuted }, setSuccessStatusFor('executeMassPayout'));
            },
            error: (error: AppError) => patchState(store, setErrorStatusFor('executeMassPayout', error)),
          }),
        );
      }),
    ),
    downloadCsv: rxMethod<{ body: DownloadCsvReq; filters: PayOutsFilters; orgId: number }>(
      switchMap(({ body, filters, orgId }) => {
        patchState(store, setPendingStatusFor('downloadCsv'));
        return massPayOutApi.downloadCsv(body, orgId).pipe(
          tapResponse({
            next: res => {
              const scoreFilter = filters.score;
              const levelOfRisk = getLevelOfRisk(scoreFilter);
              const filename = generateFilename(levelOfRisk);
              fileSave.save(res.body as Blob, filename);
              patchState(store, setSuccessStatusFor('downloadCsv'));
            },
            error: (error: AppError) => patchState(store, setErrorStatusFor('downloadCsv', error)),
          }),
        );
      }),
    ),
  })),
  withHooks({
    onInit(store) {
      store.spinnerService.observe(toObservable(store.isLoading));
      effect(() => {
        if (store.error()) {
          untracked(() => store.storeHelperService.handleError(store.error()));
        }
      });
    },
  }),
);

function combineMassPayOutData(responses: MassPayOut[]): MassPayOut {
  return responses.reduce(
    (acc, response) => {
      if (response && response.massPayOut) {
        acc.massPayOut.push(...response.massPayOut);
        acc.totalFee = (parseFloat(acc.totalFee) + parseFloat(response.totalFee)).toString();
        acc.totalAmount = (parseFloat(acc.totalAmount) + parseFloat(response.totalAmount)).toString();
        acc.ourEstimatedFee = response.ourEstimatedFee;
        acc.balanceAvailable = response.balanceAvailable;
        acc.networkBalance = response.networkBalance;
      }
      return acc;
    },
    { massPayOut: [], totalFee: '0', totalAmount: '0', ourEstimatedFee: '0', balanceAvailable: '0', networkBalance: '0' },
  );
}

export function getLevelOfRisk(scoreFilter: string[]): string {
  if (scoreFilter[0] === 'high' && scoreFilter.length === 1) {
    return 'High risk check';
  } else if (scoreFilter[0] === 'low' && scoreFilter.length === 1) {
    return 'Safe score';
  } else {
    return 'Score';
  }
}

function generateFilename(levelOfRisk: string): string {
  return `${levelOfRisk} results, ${dayjs().format('DD.MM.YYYY HH[h]mm[m]')}.csv`;
}
